import { useMarketplaceStore } from "../../store/useMarketplaceStore";
import { useParams } from "react-router-dom";
import { useCustom } from "@refinedev/core";
import {
  Typography,
  Paper,
  Grid2,
  Stack,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import { OrderDelivery } from "./components/OrderDelivery";
import { OrderItemCard } from "./components/OrderItem";
import { OrderAddress } from "./components/OrderAddress";
import { useTranslation } from "../../hooks/useTranslation";
import { Order } from "../orders/utils/types";
import { Sidebar } from "./components/sidebar/Sidebar";
import { OrderNotes } from "./components/OrderNotes";
import { ErrorOutline, Flag } from "@mui/icons-material";
import { OrderSupplyOrders } from "./components/OrderSupplyOrders";
import { SyncrosaleLoader } from "../../components/layout/components/SyncrosaleLoader";
import { SupplyOrderHistory } from "./components/SupplyOrderHistory";
import { OrderProfit } from "./components/orderProfit/OrderProfit";
import { WarehouseMessages } from "./components/WarehouseMessages";

export const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation("pages.orders.detail");

  const url = selectedCountry
    ? `store/${selectedCountry.id}/order/${orderId}`
    : "";

  const { data: orderResponse, isLoading } = useCustom<Order>({
    url: url,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry && !!orderId,
      queryKey: ["order-detail", selectedCountry?.id, orderId],
      staleTime: 0,
      cacheTime: 0,
    },
  });

  if (isLoading) {
    return <SyncrosaleLoader />;
  }
  if (!orderResponse?.data) {
    return <Typography variant="h6">{translate("orderNotFound")}</Typography>;
  }

  const order = orderResponse.data;

  const isPreconfirmed = order?.deliveryOrder?.shipment?.trackingNumber === "";

  const hasNotes = order.notes.length > 0;
  const hasSupplyOrders = order.supplyOrders.length > 0;
  const infoPanelCount = [hasNotes, true, hasSupplyOrders].filter(
    Boolean,
  ).length;

  const infoPanelLgSize =
    infoPanelCount <= 1 ? 12 : infoPanelCount === 2 ? 6 : 4;

  const orderItemsLgSize = 12;

  return (
    <Box sx={{ padding: 3 }}>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        mb={1}
        spacing={2}
      >
        <Typography variant="h5" textAlign="center">
          {translate("title")}
        </Typography>

        {isPreconfirmed && (
          <Tooltip title={translate("preconfirmedDesc")}>
            <Chip
              icon={<ErrorOutline />}
              label={translate("preconfirmed")}
              color="warning"
              size="small"
              sx={{
                ml: 1,
                fontWeight: 500,
                "& .MuiChip-icon": {
                  fontSize: "1.2rem",
                  ml: 0.2,
                },
              }}
            />
          </Tooltip>
        )}
        {order.orderFlag && (
          <Chip
            icon={
              <Flag
                sx={{
                  color: `${order.orderFlag.userFlag.flagColor} !important`,
                  fontSize: "1.3rem",
                }}
              />
            }
            label={order.orderFlag.userFlag.flagName}
            size="medium"
            sx={{
              backgroundColor: `${order.orderFlag.userFlag.flagColor}18`,
              border: "1px solid",
              borderColor: `${order.orderFlag.userFlag.flagColor}40`,
              color: order.orderFlag.userFlag.flagColor,
              fontWeight: 600,
              fontSize: "0.85rem",
              height: 32,
              borderRadius: "16px",
            }}
          />
        )}
      </Stack>
      <Grid2 container spacing={2}>
        <Grid2
          container
          size={{ xs: 12, lg: 10 }}
          spacing={2}
          direction="row"
          sx={{
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
        >
          <Grid2 component={Paper} size={{ xs: 12, lg: 4 }}>
            <OrderDelivery
              deliveryOrder={order.deliveryOrder}
              orderStatus={order.orderStatus}
              barcode={order.barcode}
              warehouseError={order.warehouseErrorMessage}
            />
          </Grid2>
          <Grid2 component={Paper} size={{ xs: 12, lg: 4 }}>
            <OrderAddress deliveryOrder={order.deliveryOrder} />
          </Grid2>
          <Grid2 component={Paper} size={{ xs: 12, lg: 4 }}>
            <OrderProfit
              key={
                order.id +
                "-" +
                order.overriddenFinancialReport?.profit +
                "-" +
                order.actualFinancialReport?.profit
              }
              Order={order}
            />
          </Grid2>
          <Grid2 container size={{ xs: 12, lg: orderItemsLgSize }}>
            {order.orderItems.map((orderItem) => (
              <Grid2 size={{ xs: 12, lg: 12 }} key={orderItem.id}>
                <OrderItemCard
                  orderItem={orderItem}
                  orderStatus={order.orderStatus}
                  warehouseErrorMessage={order.warehouseErrorMessage}
                />
              </Grid2>
            ))}
          </Grid2>

          {hasNotes && (
            <Grid2
              component={Paper}
              size={{ xs: 12, lg: infoPanelLgSize }}
              p={1}
            >
              <OrderNotes orderNotes={order.notes} />
            </Grid2>
          )}

          <Grid2 component={Paper} size={{ xs: 12, lg: infoPanelLgSize }} p={1}>
            <WarehouseMessages warehouseMessages={order.warehouseMessages} />
          </Grid2>

          {hasSupplyOrders && (
            <Grid2
              component={Paper}
              size={{ xs: 12, lg: infoPanelLgSize }}
              p={1}
            >
              <OrderSupplyOrders order={order} />
            </Grid2>
          )}

          {order.history && <SupplyOrderHistory order={order} />}
        </Grid2>

        <Grid2 component={Paper} size={{ xs: 12, lg: 2 }} p={1}>
          <Sidebar order={order} />
        </Grid2>
      </Grid2>
    </Box>
  );
};
